import {AxiosCall, CancelTokenSource} from '../axios';
import {serializeQueryParams} from './PaymentHistoryService';

export const fetchDueInvoices = async (
  clinic_group_id: number | string,
  clinic_id: number | string,
  options?: any,
  limit?: number,
  offset?: number,
  sort?: {sort: string; order: string},
  group_search: boolean = false,
  settled: boolean = false,
  cancelToken?: CancelTokenSource
): Promise<any> => {
  let params = '';
  if (sort?.sort) {
    params = `sort_by=${sort.sort}&order=${sort.order || ''}`;
  }
  return AxiosCall.post(
    `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/due-invoices?limit=${
      limit || ''
    }&offset=${
      offset || ''
    }&${params}&group_search=${group_search}&is_settled=${settled}`,
    options ? options : {},
    cancelToken
  );
};

export const fetchDueInvoicesPatientCount = async (
  clinic_group_id: number | string,
  clinic_id: number | string,
  options?: any,
  group_search: boolean = false,
  settled: boolean = false,
  cancelToken?: CancelTokenSource
): Promise<any> => {
  return AxiosCall.post(
    `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/due-invoices/patients-count?group_search=${group_search}&is_settled=${settled}`,
    options ? options : {},
    cancelToken
  );
};

export const invoiceDetails = async (
  clinic_group_id: number | string,
  clinicId: number | string,
  patient_id: number | string,
  group_search: boolean = false
): Promise<any> => {
  return AxiosCall.get(
    `/clinic-group/${clinic_group_id}/patient/${patient_id}/invoices?clinic_id=${clinicId}&group_search=${group_search}`
  );
};

export const pastInvoiceDetails = async (
  clinic_group_id: number | string,
  clinic_id: number | string,
  patient_id: number | string
): Promise<any> => {
  return AxiosCall.get(
    `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/patient/${patient_id}/previous-statements`
  );
};

export const getAllReminders = async (
  clinic_group_id: number | string,
  guarantor_id: number | string
): Promise<any> => {
  return AxiosCall.get(
    `/clinic-group/${clinic_group_id}/guarantor/${guarantor_id}/reminder`
  );
};

export const allSettleInvoices = async (
  clinic_group_id: number | string,
  clinic_id: number | string,
  options?: any,
  limit?: number,
  offset?: number,
  sort?: {sort: string; order: string}
): Promise<any> => {
  let params = '';
  if (sort?.sort) {
    params = `sort_by=${sort.sort}&order=${sort.order || ''}`;
  }
  return AxiosCall.post(
    `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/settled-invoices?limit=${
      limit || ''
    }&offset=${offset || ''}&${params}`,
    options ? options : {}
  );
};

export const sendReminders = async (
  clinic_group_id: number | string,
  clinic_id: number | string,
  payload: {
    guarantors: number[] | string[];
    reminder_type: string[];
    select_all: boolean;
    due_type: string;
    notification_count?: number;
    payment_flow_status?: string;
    message?: string;
    includeSalutation?: boolean;
  }
): Promise<any> => {
  return AxiosCall.post(
    `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/guarantor/due-reminder/send`,
    payload
  );
};

export const scheduleReminders = async (
  clinic_group_id: number | string,
  clinic_id: number | string,
  payload: {
    guarantors: number[] | string[];
    reminder_date: string | Date;
    reminder_type: string[];
    select_all: boolean;
    due_type: string;
    notification_count?: number;
    payment_flow_status?: string;
    message?: string;
    includeSalutation?: boolean;
  }
): Promise<any> => {
  return AxiosCall.post(
    `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/guarantor/due-reminder/schedule`,
    payload
  );
};

export const downloadFNPDTemplate = async (): Promise<any> => {
  return AxiosCall.get('/file/template/due_notice_type');
};

export const updateGuarantorDueNoticeType = async (
  clinic_group_id: number | string,
  clinic_id: number | string,
  guarantor_id: number | string,
  payload: {
    due_notice_type: string;
  }
): Promise<any> => {
  return AxiosCall.put(
    `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/guarantor/${guarantor_id}/statement/update-due-notice-type`,
    payload
  );
};

export const updateGuarantorNotificationPhone = async (
  clinic_group_id: number | string,
  guarantor_id: number | string,
  payload: {
    notification_phone: string | undefined;
  }
): Promise<any> => {
  return AxiosCall.put(
    `/clinic-group/${clinic_group_id}/guarantor/${guarantor_id}/notification-phone`,
    payload
  );
};

export const downloadRemindersFile = async (
  clinic_group_id: number | string,
  clinic_id: number | string,
  options: any
): Promise<any> => {
  const queryParams = serializeQueryParams(options);
  return AxiosCall.get(
    `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/guarantor/due-reminder/download?${queryParams}`
  );
};

export const sendReminderToGuarantor = async (
  clinic_group_id: number | string,
  clinic_id: number | string,
  guarantor_id: string | number,
  payload: {
    email?: string;
    phone?: string;
    reminder_type: string;
  }
): Promise<any> => {
  return AxiosCall.post(
    `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/guarantor/${guarantor_id}/send-payment-reminder`,
    payload
  );
};

export const updateGuarantorDueAsSettleStatement = async (
  clinic_group_id: number | string,
  clinic_id: number | string,
  guarantor_id: number | string,
  payload: {
    settled_reason: string;
  }
): Promise<any> => {
  return AxiosCall.put(
    `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/guarantor/${guarantor_id}/statement/update-to-settle-statement`,
    payload
  );
};

export const downloadDueInvoices = async (
  clinic_group_id: number | string,
  clinic_id: number | string,
  options?: any,
  sort?: {sort: string; order: string}
): Promise<any> => {
  let params = '';
  if (sort?.sort) {
    params = `sort_by=${sort.sort}&order=${sort.order || ''}`;
  }
  return AxiosCall.post(
    `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/due-invoices/download?${params}`,
    options ? options : {}
  );
};

export const generateThePaymentToken = async (
  clinic_group_id: string | number,
  guarantor_id: string | number
): Promise<any> => {
  return AxiosCall.put(
    `/clinic-group/${clinic_group_id}/guarantor/${guarantor_id}/payment-token`,
    {}
  );
};

export const getGuarantorPaymentPlanDetails = async (
  clinic_group_id: number | string,
  clinicId: number | string,
  guarantor_id: number | string
): Promise<any> => {
  return AxiosCall.get(
    `/clinic-group/${clinic_group_id}/clinic/${clinicId}/guarantor/${guarantor_id}/payment-plan-details`
  );
};

export const cancelCurrentPaymentPlan = async (
  clinic_group_id: number | string,
  clinicId: number | string,
  values: {
    guarantor_id: number | string;
    payment_plan_id: number | string;
  }
): Promise<any> => {
  return AxiosCall.put(
    `/clinic-group/${clinic_group_id}/clinic/${clinicId}/guarantor-payment-plan/cancel`,
    values
  );
};

export const getPaymentPlanDetails = async (
  clinic_group_id: number | string,
  clinicId: number | string,
  options?: any
): Promise<any> => {
  const queryParams = serializeQueryParams(options);
  return AxiosCall.get(
    `/clinic-group/${clinic_group_id}/clinic/${clinicId}/get-payment-plan?${queryParams}`,
    options.cancelToken
  );
};
export const updateWorkFlowStatus = async (
  clinic_group_id: number | string,
  clinic_id: number | string,
  reminder_id: number,
  work_flow_status: string,
  options: any
): Promise<any> => {
  const queryParams = serializeQueryParams(options);
  return AxiosCall.post(
    `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/guarantor/due-reminder/${reminder_id}/work-flow-status?${queryParams}`,
    {work_flow_status}
  );
};

export const getGuarantorBalancesFromMultipleLocations = async (
  clinic_group_id: number | string,
  guarantor_id: number | string,
  clinic_id: number | string,
  include_settled_dues: boolean = false
): Promise<any> => {
  let url = `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/guarantor/${guarantor_id}/get-guarantor-balances-from-multiple-locations`;
  url += `?include_settled_dues=${include_settled_dues}`;
  return AxiosCall.get(url);
};

export const getGuarantorDueBalancesGuarantorPortal = async (
  guarantor_id: number | string
): Promise<any> => {
  const url = `/guarantor/due-balances`;
  return AxiosCall.get(url);
};

export const fetchGuarantorInvoices = async (
  clinic_group_id: number | string,
  clinic_id: number | string,
  guarantor_id: number | string
): Promise<any> => {
  return AxiosCall.get(
    `/clinic-group/${clinic_group_id}/clinic_id/${clinic_id}/guarantor/${guarantor_id}/invoices`
  );
};

export const createInvoice = async ({
  clinic_group_id,
  clinic_id,
  payload
}: {
  payload: {
    patient_id: number | string;
    statement_month: number;
    statement_year: number;
    invoice_number: number | string;
    invoice_date: string;
    date_of_service: string;
    total_due: number;
    invoice_lines: {
      description: string;
      date_of_service: string;
      amount: number;
    }[];
  };
  clinic_group_id: number | string;
  clinic_id: number | string;
}) => {
  return AxiosCall.post(
    `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/invoice`,
    payload
  );
};

export const updateInvoice = async ({
  clinic_group_id,
  clinic_id,
  payload
}: {
  payload: {
    patient_id: number | string;
    statement_month: number;
    statement_year: number;
    invoice_number: number | string;
    invoice_date: string;
    date_of_service: string;
    total_due: number;
    invoice_lines: {
      description: string;
      date_of_service: string;
      amount: number;
    }[];
  };
  clinic_group_id: number | string;
  clinic_id: number | string;
}) => {
  return AxiosCall.put(
    `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/invoice`,
    payload
  );
};

export const settleThePatientCopay = async (
  clinic_group_id: number | string,
  clinic_id: number | string,
  copay_id: number | string,
  payload: {
    settled_reason: string;
  }
): Promise<any> => {
  return AxiosCall.put(
    `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/patient-copay/${copay_id}/settle`,
    payload
  );
};

export const fetchGuarantorsInvoiceNotificationDetails = ({
  payload,
  clinic_group_id,
  clinic_id,
  cancelToken
}: {
  payload: {
    guarantor_ids: number[];
  };
  clinic_group_id: number;
  clinic_id: number;
  cancelToken?: CancelTokenSource;
}): any => {
  return AxiosCall.post(
    `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/due-invoices/notifications`,
    payload,
    cancelToken
  );
};
